/**
 * Painel PAEBM abaixo da lista de instrumentos no Mapa (M2) — GUPM-195.
 * ------------------------------------------------------------------------
 * Referência de UI: protótipo V5_4_4 (`res-alert-panel`, fim da tela de Mapa),
 * adaptada ao modelo canônico V8 "edificações SEM moradores fixos" → SEM PII
 * (nada de morador/telefone/WhatsApp — a barreira LGPD do V5_4_4 não se aplica).
 * Complementa as CAMADAS do mapa: aqui é a LISTA (cards) das mesmas edificações
 * ZAS + pontos de encontro, com os campos ricos do banco (usePaebmMapa).
 *
 * Puro: recebe tudo por props (dado já vem do hook no index). Zero I/O.
 */
import type { PontoEncontro, ZasEdificacao } from '@/domain';
import { PONTO_ENCONTRO_COLOR } from '@/domain';
import AssemblyPointIcon from './AssemblyPointIcon';

interface Props {
  edificacoes: ZasEdificacao[];
  pontosEncontro: PontoEncontro[];
}

const PILL = 'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide';

/** Card de uma edificação na ZAS — sem PII (só infraestrutura + destino de evacuação). */
function EdificacaoCard({ e }: { e: ZasEdificacao }) {
  return (
    <li className="rounded-lg border border-jf-border bg-jf-surface p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-bold text-white" title={e.label}>
            🏠 {e.label}
          </div>
          {e.nota && <p className="mt-1 text-[11px] leading-snug text-jf-muted">{e.nota}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {e.ne3 && <span className={`${PILL} bg-level-atencao/15 text-level-atencao`}>Evacuação NE-3</span>}
          <span
            className={`${PILL} ${e.sinore ? 'bg-jf-yellow/15 text-jf-yellow' : 'bg-jf-border/40 text-jf-dim'}`}
          >
            {e.sinore ? 'SINORE ✓' : 'sem SINORE'}
          </span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-jf-border pt-2 text-[10px] text-jf-dim">
        {e.distancia && <span>📍 {e.distancia}</span>}
        {e.pontoEncontro && (
          <span className="inline-flex items-center gap-1">
            <AssemblyPointIcon size={13} />
            Destino: <strong className="text-white">{e.pontoEncontro}</strong>
          </span>
        )}
        <span className="font-mono">
          WGS84 {e.lat.toFixed(5)}, {e.lon.toFixed(5)}
        </span>
      </div>

      {/* Alerta direto (V5_4_4): placeholders desabilitados — sem telefone real
          ainda (modelo V8 sem morador). Ativam quando o contato real for cadastrado. */}
      <div className="mt-2">
        <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-jf-dim">
          Alerta direto · número a cadastrar
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled
            title="Número de contato a cadastrar (aguardando dado real)"
            className="flex-1 rounded-md border border-jf-border bg-transparent px-2 py-1 text-[10px] font-bold text-jf-dim opacity-60 disabled:cursor-not-allowed"
          >
            📱 WhatsApp
          </button>
          <button
            type="button"
            disabled
            title="Número de contato a cadastrar (aguardando dado real)"
            className="flex-1 rounded-md border border-jf-border bg-transparent px-2 py-1 text-[10px] font-bold text-jf-dim opacity-60 disabled:cursor-not-allowed"
          >
            📞 Ligar
          </button>
        </div>
      </div>
    </li>
  );
}

/** Card de um ponto de encontro — cor do marcador (interno azul / externo laranja) fiel à legenda. */
function PontoEncontroCard({ p }: { p: PontoEncontro }) {
  const cor = PONTO_ENCONTRO_COLOR[p.tipo];
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-jf-border bg-jf-surface p-3">
      <AssemblyPointIcon size={24} color={cor} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-white">{p.label}</span>
          <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: cor }}>
            {p.tipo}
          </span>
        </div>
        {p.descricao && <p className="mt-0.5 text-[11px] leading-snug text-jf-muted">{p.descricao}</p>}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-jf-dim">
          {p.capacidade && (
            <span>
              Capacidade: <strong className="text-jf-muted">{p.capacidade}</strong>
            </span>
          )}
          {p.tempoRota && (
            <span>
              Rota: <strong className="text-jf-muted">{p.tempoRota}</strong>
            </span>
          )}
          {p.nivel && <span>Aciona em {p.nivel}</span>}
          {p.acesso && <span>· {p.acesso}</span>}
        </div>
      </div>
    </li>
  );
}

/** Seção "Edificações ZAS + Pontos de encontro" no fim da tela de Mapa (após a lista de instrumentos). */
export default function PaebmZasPanel({ edificacoes, pontosEncontro }: Props) {
  if (edificacoes.length === 0 && pontosEncontro.length === 0) return null;

  return (
    <section className="mt-6 space-y-4">
      {/* ── Edificações na ZAS ─────────────────────────────────────────── */}
      <div>
        <h2 className="mb-2 text-sm font-bold text-white">
          🏠 Edificações na ZAS <span className="font-normal text-jf-dim">({edificacoes.length})</span>
        </h2>
        <div className="mb-2 rounded-lg border border-level-atencao/30 bg-level-atencao/10 px-3 py-2 text-[11px] leading-relaxed text-level-atencao">
          ⚠️ <strong>PAEBM Seção 10:</strong> edificações na Zona de Autossalvamento (ZAS) — evacuação
          preventiva em <strong>NE-3</strong>. Modelo sem moradores fixos; alerta por sirene/SINORE.
        </div>
        {edificacoes.length > 0 ? (
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {edificacoes.map((e) => (
              <EdificacaoCard key={e.codigo} e={e} />
            ))}
          </ul>
        ) : (
          <p className="text-[11px] italic text-jf-dim">Nenhuma edificação cadastrada.</p>
        )}
      </div>

      {/* ── Pontos de encontro ─────────────────────────────────────────── */}
      <div>
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-white">
          <AssemblyPointIcon size={18} color="#16a34a" />
          Pontos de Encontro{' '}
          <span className="font-normal text-jf-dim">(PAEBM Seção 8 · {pontosEncontro.length})</span>
        </h2>
        {pontosEncontro.length > 0 ? (
          <ul className="grid gap-2 sm:grid-cols-2">
            {pontosEncontro.map((p) => (
              <PontoEncontroCard key={p.codigo} p={p} />
            ))}
          </ul>
        ) : (
          <p className="text-[11px] italic text-jf-dim">Nenhum ponto de encontro cadastrado.</p>
        )}
      </div>
    </section>
  );
}
